import { motion } from "motion/react";
import { DropdownMenu } from "radix-ui";
import { CaretDown, Check } from "@phosphor-icons/react";
import type { AppViewStatus, Outcome } from "../../shared/types.ts";
import { APP_STATUS, ToneDot, cn, spring, type Tone } from "../ui/index.ts";

type Choice = Outcome | "none";

/** What came back from an application: label and tone. No outcome reads as no reply. */
export const OUTCOME: Record<Choice, { label: string; tone: Tone }> = {
  none: { label: "No reply", tone: "gray" },
  rejected: { label: "Rejected", tone: "red" },
  interview: { label: "Interview", tone: "mint" },
  offer: { label: "Offer", tone: "accent" },
  no_offer: { label: "No offer", tone: "cream" },
};

const ORDER: Choice[] = ["none", "rejected", "interview", "offer", "no_offer"];

const FILL: Record<Tone, string> = {
  brand: "bg-tone-brand text-tone-brand-ink hover:bg-primary-tint",
  mint: "bg-tone-mint text-tone-mint-ink hover:bg-[color-mix(in_srgb,var(--tone-mint-ink)_14%,var(--tone-mint))]",
  accent: "bg-tone-accent text-tone-accent-ink hover:bg-[color-mix(in_srgb,var(--tone-accent-ink)_12%,var(--tone-accent))]",
  cream: "bg-tone-cream text-tone-cream-ink hover:bg-[color-mix(in_srgb,var(--tone-cream-ink)_12%,var(--tone-cream))]",
  amber: "bg-tone-amber text-tone-amber-ink",
  gray: "bg-tone-gray text-tone-gray-ink hover:bg-[color-mix(in_srgb,var(--tone-gray-ink)_10%,var(--tone-gray))]",
  red: "bg-tone-red text-tone-red-ink hover:bg-[color-mix(in_srgb,var(--tone-red-ink)_12%,var(--tone-red))]",
};

const DOT: Record<Tone, string> = {
  brand: "text-primary",
  mint: "text-ok",
  accent: "text-accent",
  cream: "text-tone-cream-ink",
  amber: "text-warn",
  gray: "text-muted",
  red: "text-bad",
};

/**
 * The badge on a sent application that also marks what came back. With nothing marked it shows the status (Applied,
 * Confirmed); once marked, the outcome in its tone.
 */
export function OutcomeMenu({ status, outcome, company, onChange }: { status: AppViewStatus; outcome: Outcome | null; company: string; onChange: (o: Outcome | null) => void }) {
  const meta = outcome ? OUTCOME[outcome] : { label: APP_STATUS[status].label, tone: APP_STATUS[status].tone };
  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger asChild>
        <motion.button
          type="button"
          whileTap={{ scale: 0.96 }}
          transition={spring}
          aria-label={`What came back from ${company}: ${outcome ? meta.label : "no reply yet"}`}
          className={cn(
            "group relative inline-flex h-7 shrink-0 items-center gap-1 whitespace-nowrap rounded-chip pl-2 pr-1.5 text-xs font-semibold transition-[background-color] duration-150",
            "before:absolute before:-inset-1.5 before:content-['']",
            FILL[meta.tone],
          )}
        >
          {status === "on_site" && !outcome ? (
            <>
              <span className="sm:hidden">Applied</span>
              <span className="hidden sm:inline">{meta.label}</span>
            </>
          ) : (
            meta.label
          )}
          <CaretDown size={12} weight="bold" aria-hidden className="opacity-70 transition-[rotate] duration-200 group-data-[state=open]:rotate-180" />
        </motion.button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="end" sideOffset={6} collisionPadding={12} className="z-50 outline-none">
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={spring}
            style={{ transformOrigin: "var(--radix-dropdown-menu-content-transform-origin)" }}
            className="min-w-44 rounded-card bg-surface p-1 shadow-raised"
          >
            <DropdownMenu.RadioGroup value={outcome ?? "none"} onValueChange={(v) => onChange(v === "none" ? null : (v as Outcome))}>
              {ORDER.map((o) => (
                <DropdownMenu.RadioItem
                  key={o}
                  value={o}
                  className="flex h-9 cursor-pointer select-none items-center gap-2.5 rounded-control px-2.5 text-[13.5px] font-semibold text-ink outline-none transition-[background-color] duration-100 data-[highlighted]:bg-sunk"
                >
                  <span className={cn("flex", DOT[OUTCOME[o].tone])}>
                    <ToneDot className="size-2" />
                  </span>
                  <span className="flex-1">{OUTCOME[o].label}</span>
                  <DropdownMenu.ItemIndicator>
                    <Check size={14} weight="bold" className="text-primary" aria-hidden />
                  </DropdownMenu.ItemIndicator>
                </DropdownMenu.RadioItem>
              ))}
            </DropdownMenu.RadioGroup>
          </motion.div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
